import React, { useState } from "react";
import { Send, CheckCircle2, Phone, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { SITE } from "../lib/site";
import { toast } from "sonner";
import PaymentStrip from "./PaymentStrip";

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", suburb: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Booked! I’ll be in touch shortly.");
    } catch (err) {
      toast.error("Couldn’t send. Please call me on " + SITE.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="py-20 md:py-24 bg-[#FFFFFF]">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-base md:text-lg font-semibold text-[#6BAF3C] uppercase tracking-wide">Book a visit</p>
          <h2 className="mt-3 font-[Work_Sans] text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F2E52]">
            Tell me how I can help.
          </h2>
          <p className="mt-4 text-lg md:text-xl text-[#1E3A5F]">
            Pop your details in below — or just ring me on{" "}
            <a className="link-underline font-semibold text-[#0F2E52]" href={`tel:${SITE.phoneTel}`}>{SITE.phone}</a>.
            I’ll get back to you the same day where I can.
          </p>
        </div>

        <div className="mt-6 md:mt-8 max-w-2xl mx-auto">
          <PaymentStrip />
        </div>

        <div className="mt-6 bg-white border border-[#DCE4EC] rounded-2xl p-6 md:p-10 shadow-sm">
          {done ? (
            <div className="text-center py-6" data-testid="booking-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#0F2E52]/15 grid place-items-center">
                <CheckCircle2 className="w-9 h-9 text-[#0F2E52]" strokeWidth={2.5} />
              </div>
              <h3 className="mt-5 font-[Work_Sans] text-2xl md:text-3xl font-bold text-[#0F2E52]">
                Thanks {form.name.split(" ")[0]} — booking received!
              </h3>
              <p className="mt-3 text-lg text-[#1E3A5F]">
                I’ll give you a call on <strong>{form.phone}</strong> to confirm a time that suits you.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`tel:${SITE.phoneTel}`}
                  data-testid="booking-success-call"
                  className="inline-flex items-center justify-center gap-2 bg-[#0F2E52] hover:bg-[#0A2340] text-white font-bold rounded-xl px-6 py-4 text-lg"
                >
                  <Phone className="w-5 h-5" /> Call me now
                </a>
                <button
                  type="button"
                  onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", suburb: "", message: "" }); }}
                  data-testid="booking-reset"
                  className="inline-flex items-center justify-center bg-white border-2 border-[#0F2E52] text-[#0F2E52] font-bold rounded-xl px-6 py-4 text-lg hover:bg-[#F0F7E8]"
                >
                  Send another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="booking-form" className="grid md:grid-cols-2 gap-5">
              <Field label="Your name" required>
                <input
                  data-testid="booking-input-name"
                  type="text" value={form.name} onChange={update("name")}
                  placeholder="e.g. Margaret Wilson"
                  className="input-senior"
                />
              </Field>
              <Field label="Phone number" required>
                <input
                  data-testid="booking-input-phone"
                  type="tel" value={form.phone} onChange={update("phone")}
                  placeholder="A number to reach you"
                  className="input-senior"
                />
              </Field>
              <Field label="Email (optional)">
                <input
                  data-testid="booking-input-email"
                  type="email" value={form.email} onChange={update("email")}
                  placeholder="you@example.com"
                  className="input-senior"
                />
              </Field>
              <Field label="Suburb">
                <input
                  data-testid="booking-input-suburb"
                  type="text" value={form.suburb} onChange={update("suburb")}
                  placeholder="e.g. Norwood"
                  className="input-senior"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Tell me a bit more (optional)">
                  <textarea
                    data-testid="booking-input-message"
                    rows={5} value={form.message} onChange={update("message")}
                    placeholder="What’s going on? Even a few words helps."
                    className="input-senior resize-y"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#0F2E52] hover:bg-[#0A2340] disabled:opacity-60 text-white font-bold rounded-xl px-6 py-5 text-xl min-h-[60px] shadow-sm"
                >
                  {submitting ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" strokeWidth={2.5} />}
                  {submitting ? "Sending..." : "Request a Home Visit"}
                </button>
                <p className="text-center text-sm md:text-base text-[#475569] mt-3">
                  No payment needed to book. Free 5-minute chat first if you’d like.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .input-senior {
          width: 100%;
          font-size: 1.125rem;
          padding: 1rem 1rem;
          border-radius: 0.75rem;
          border: 2px solid #DCE4EC;
          background: #fff;
          color: #0F2E52;
          outline: none;
        }
        .input-senior:focus {
          border-color: #0F2E52;
        }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-base md:text-lg font-semibold text-[#0F2E52] mb-2">
        {label}{required && <span className="text-[#0F2E52]"> *</span>}
      </span>
      {children}
    </label>
  );
}
